import { invariant } from '@seen/core/invariant'
import type * as appointments from '@seen/core/models/appointments/index'
import { DotSeparator } from '@seen/ui'
import { compact } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import {
  Avatar,
  Combobox,
  type ComboboxProps,
} from '#src/components/core/index.ts'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'

export function AppointmentCombobox({
  appointments,
  onSelect,
  ...delegated
}: {
  appointments: appointments.ExtendedAppointment[]
  onSelect: (value: appointments.ExtendedAppointment) => any
} & Omit<ComboboxProps<string>, 'options' | 'onSelect'>): JSX.Element {
  const options = useMemo(
    () =>
      appointments.map((appointment) => ({
        value: appointment.reference.reference,
        label: (
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1">
              <div className="flex-1 truncate">{appointment.getDisplay()}</div>
              <DotSeparator />
              <DateTimeDisplay value={appointment.start} />
            </div>
            {appointment.practitioners.length > 0 && (
              <Avatar profile={appointment.practitioners[0]} size="icon" />
            )}
          </div>
        ),
        keywords: compact([appointment.getDisplay(), appointment.start]),
      })),
    [appointments]
  )

  return (
    <Combobox
      options={options}
      onSelect={(value) => {
        const appointment = appointments.find(
          (a) => a.reference.reference === value
        )
        invariant(appointment, 'Appointment is required')
        onSelect(appointment)
      }}
      {...delegated}
    />
  )
}
